import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Save, ArrowRight, CheckCircle2 } from 'lucide-react';
import type { Asset } from '@/types/asset';

interface AssetConfirmChangesModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly originalAsset: Asset;
  readonly editedAsset: Asset;
  readonly onConfirm: () => void;
}

interface ChangeItem {
  field: string;
  oldValue: string;
  newValue: string;
}

const formatFieldName = (field: string): string => {
  // Convert camelCase to Title Case
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const generateChangeSummary = (originalAsset: Asset, editedAsset: Asset): ChangeItem[] => {
  const changes: ChangeItem[] = [];
  
  // Fields to exclude from comparison
  const excludeFields = ['modifiedBy', 'modifiedDate', 'createdBy', 'createdDate', 'id'];
  
  // Compare all fields
  Object.keys(editedAsset).forEach((key) => {
    if (excludeFields.includes(key)) return;
    
    const field = key as keyof Asset;
    const originalValue = originalAsset[field];
    const editedValue = editedAsset[field];
    
    // Deep comparison for objects and arrays
    const originalStr = JSON.stringify(originalValue);
    const editedStr = JSON.stringify(editedValue);
    
    if (originalStr !== editedStr) {
      changes.push({
        field: formatFieldName(field),
        oldValue: formatValue(originalValue),
        newValue: formatValue(editedValue),
      });
    }
  });
  
  return changes;
};

export function AssetConfirmChangesModal({
  open,
  onOpenChange,
  originalAsset,
  editedAsset,
  onConfirm,
}: AssetConfirmChangesModalProps) {
  const changes = generateChangeSummary(originalAsset, editedAsset);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Save className="h-5 w-5 text-primary" />
            Confirm Changes
          </DialogTitle>
          <DialogDescription>
            Review the changes you're about to save to this asset record.
          </DialogDescription>
        </DialogHeader>

        {changes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-6">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No changes detected
            </p>
          </div>
        ) : (
          <>
            <div className="px-6 pb-3 flex items-center justify-between flex-shrink-0 border-b">
              <p className="text-sm font-medium">
                {changes.length} field{changes.length === 1 ? '' : 's'} modified
              </p>
              <Badge variant="secondary">
                {changes.length}
              </Badge>
            </div>
            
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
              <div className="space-y-3">
                {changes.map((change) => (
                  <div
                    key={`change-${change.field}`}
                    className="rounded-md border bg-card p-3 space-y-1.5"
                  >
                    <div className="text-xs font-medium text-foreground">
                      {change.field}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <div className="flex-1 flex items-center gap-1.5">
                        <span className="text-muted-foreground">From:</span>
                        <code className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono text-xs break-all">
                          {change.oldValue}
                        </code>
                      </div>
                      <ArrowRight className="h-3 w-3 text-primary flex-shrink-0" />
                      <div className="flex-1 flex items-center gap-1.5">
                        <span className="text-muted-foreground">To:</span>
                        <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono font-medium text-xs break-all">
                          {change.newValue}
                        </code>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <DialogFooter className="px-6 py-4 gap-2 sm:gap-0 flex-shrink-0 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={changes.length === 0}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Confirm & Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
